import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Button from '../../common/Button';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';

const EditManagerModal = ({ isOpen, onClose, onSubmit, manager }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    designation: '',
    is_active: true,
    password: '',
    is_locked_out: false
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (manager && isOpen) {
      setFormData({
        name: manager.name || '',
        email: manager.email || '',
        contact: manager.contact || '',
        designation: manager.designation || '',
        is_active: manager.is_active !== undefined ? manager.is_active : true,
        password: '',
        is_locked_out: manager.is_locked_out !== undefined ? manager.is_locked_out : false
      });
    }
  }, [manager, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await onSubmit(manager.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating manager:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Manager'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit Manager - ${manager?.name}`} size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <Input label="Full Name" name="name" placeholder="e.g., John Doe" value={formData.name} onChange={handleChange} error={errors.name} required />
        <Input label="Designation" name="designation" placeholder="Senior Manager" value={formData.designation} onChange={handleChange} error={errors.designation} />
        <div style={gridStyle}>
          <Input label="Email" name="email" type="email" placeholder="john@amc.com" value={formData.email} onChange={handleChange} error={errors.email} required />
          <Input label="Phone Number" name="contact" placeholder="9876543210" value={formData.contact} onChange={handleChange} error={errors.contact} required />
        </div>

        <Input label="New Password" name="password" type="password" canPeekPassword placeholder="Leave blank to keep unchanged" value={formData.password} onChange={handleChange} error={errors.password} />


        <label style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: formData.is_locked_out ? '#fee2e2' : 'var(--surface-light)', border: formData.is_locked_out ? '1px solid #ef4444' : 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '8px'
        }}>
          <input type="checkbox" name="is_locked_out" checked={formData.is_locked_out} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
          <span style={{ fontSize: '14px', color: formData.is_locked_out ? '#ef4444' : 'var(--text-primary)', fontWeight: '500' }}>Account Locked</span>
        </label>
      </form>
    </Modal>
  );
};

export default EditManagerModal;
